import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useCart } from "@/context/CartContext";
import { api, formatGBP } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { Plus, Minus, Trash2, ShoppingBag, Scissors } from "lucide-react";
import { FreeDeliveryBar, FREE_DELIVERY_THRESHOLD } from "@/components/FreeDeliveryBar";
import { ProductCard } from "@/components/ProductCard";
import { CustomFitSummary } from "@/components/CustomiseFit";

export default function CartPage() {
  const { cart, updateItem, removeItem } = useCart();
  const { format } = useCurrency();
  const customFeeTotal = (cart.items || []).reduce((s, it) => s + (it.custom_fee || 0), 0);
  const shipping = cart.subtotal >= FREE_DELIVERY_THRESHOLD || cart.subtotal === 0 ? 0 : 4.99;
  const total = cart.subtotal + shipping;

  const [recos, setRecos] = useState([]);
  useEffect(() => {
    api.get("/products").then(({ data }) => {
      const inCart = new Set(cart.items.map((i) => i.slug));
      setRecos(data.filter((p) => !inCart.has(p.slug)).slice(0, 4));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.items.length]);

  if (cart.items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-28 text-center" data-testid="cart-page">
        <ShoppingBag className="w-14 h-14 text-gray-200 mx-auto" />
        <h1 className="font-serif text-4xl mt-6 text-charcoal">Your Bag is Empty</h1>
        <p className="text-gray-500 mt-3">Discover our latest collections and find something you love.</p>
        <Link to="/shop/new-in" className="inline-block mt-8 bg-pink text-white px-9 py-4 text-xs uppercase tracking-[0.18em] hover:bg-pink-hover transition-colors">
          Shop New In
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="cart-page">
      <h1 className="font-serif text-5xl text-charcoal mb-10">Shopping Bag</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-6">
          {cart.items.map((item) => (
            <div key={item.line_id} className="flex gap-5 border-b border-gray-100 pb-6" data-testid={`cart-row-${item.slug}`}>
              <Link to={`/product/${item.slug}`}>
                <img src={item.image} alt={item.name} className="w-28 h-36 object-cover bg-surface" />
              </Link>
              <div className="flex-1">
                <div className="flex justify-between">
                  <div>
                    <Link to={`/product/${item.slug}`} className="font-serif text-xl hover:text-pink transition-colors">{item.name}</Link>
                    <p className="text-sm text-gray-500">{item.subtitle}</p>
                    {(item.colour || item.size) && (
                      <p className="text-sm text-gray-400 mt-0.5">{[item.colour, item.size].filter(Boolean).join(" · ")}</p>
                    )}
                    <CustomFitSummary customization={item.customization} />
                    <p className="text-sm font-medium mt-1">{format(item.price)}</p>
                  </div>
                  <button onClick={() => removeItem(item.line_id)} data-testid={`cart-remove-${item.slug}`} aria-label="Remove">
                    <Trash2 className="w-5 h-5 text-gray-400 hover:text-pink transition-colors" />
                  </button>
                </div>
                <div className="flex items-center justify-between mt-4">
                  <div className="flex items-center border border-gray-200">
                    <button onClick={() => updateItem(item.line_id, item.quantity - 1)} className="px-3 py-2 hover:text-pink"><Minus className="w-3.5 h-3.5" /></button>
                    <span className="px-4 text-sm">{item.quantity}</span>
                    <button onClick={() => updateItem(item.line_id, item.quantity + 1)} className="px-3 py-2 hover:text-pink"><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                  <span className="font-medium">{format(item.line_total)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-surface p-8 h-fit sticky top-28" data-testid="cart-summary">
          <h2 className="font-serif text-2xl mb-6">Order Summary</h2>
          <FreeDeliveryBar subtotal={cart.subtotal} className="mb-5" />
          <div className="space-y-3 text-sm">
            {customFeeTotal > 0 ? (
              <>
                <div className="flex justify-between"><span className="text-gray-500">Items</span><span>{format(cart.subtotal - customFeeTotal)}</span></div>
                <div className="flex justify-between" data-testid="cart-tailoring-line">
                  <span className="text-gray-500 flex items-center gap-1.5"><Scissors className="w-3.5 h-3.5 text-gold" /> Custom tailoring</span>
                  <span>+{format(customFeeTotal)}</span>
                </div>
                <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{format(cart.subtotal)}</span></div>
              </>
            ) : (
              <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{format(cart.subtotal)}</span></div>
            )}
            <div className="flex justify-between"><span className="text-gray-500">Shipping</span><span>{shipping === 0 ? "Free" : format(shipping)}</span></div>
            <p className="text-xs text-gray-400 -mt-1">Royal Mail Tracked 48 · 2–3 working days</p>
            <div className="w-full h-px bg-gray-200 my-2" />
            <div className="flex justify-between text-lg font-medium"><span>Total</span><span>{format(total)}</span></div>
          </div>
          <Link to="/checkout" data-testid="cart-checkout-btn" className="mt-6 flex items-center justify-center gap-2 w-full bg-pink text-white py-4 rounded-full text-sm font-medium shadow-sm hover:bg-pink-hover hover:shadow-md transition-all">
            Proceed to checkout
          </Link>
          <Link to="/shop/all" className="block text-center mt-4 text-xs uppercase tracking-[0.15em] text-charcoal hover:text-pink transition-colors">Continue Shopping</Link>
        </div>
      </div>

      {recos.length > 0 && (
        <section className="mt-20" data-testid="cart-recommendations">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.28em] text-pink font-semibold">Handpicked For You</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-charcoal mt-2">You May Like</h2>
            <div className="w-16 h-px bg-metallic-gold mx-auto mt-4" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
            {recos.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}
